/** @format */
import { css } from 'styled-components';

import { theme } from '@toucan/client/global/theme';

import { BufferedMeasurementsStatus } from '@toucan/client/shared/hooks/use-buffered-mesurements';

import { StyledProductCardProps } from './index';
import {
    IMAGE_CONTAINER_HEIGHT,
    THUMBNAIL_CONTAINER_GAP,
    getDetailsContainerGap,
    getDetailsContainerMarginTop,
} from './style-helpers';

/**
 * Base styles for the product card component
 */
const baseStyles = ({
    productCardBreakpointsState,
    measurements,
    shouldShowProductBox,
}: StyledProductCardProps) => {
    const backgroundPaddingSize = 'var(--layout-spacing-16)';

    const staticContainerHeight = `${measurements.staticContainer.height}px`;

    const detailsContainerGap = getDetailsContainerGap(
        productCardBreakpointsState
    );

    const detailsContainerMarginTop = getDetailsContainerMarginTop(
        productCardBreakpointsState,
        measurements.highlightsContainer.height,
        measurements.boxListHeader.height
    );

    return css`
        display: flex;
        flex-direction: column;
        height: 100%;

        .results-container {
            display: flex;
            flex-direction: column;
            gap: 0.8rem;
            width: 100%;
            position: relative;
            height: 100%;
            flex-grow: 1;

            ${productCardBreakpointsState.small &&
            css`
                flex-direction: row;
                gap: var(--layout-spacing-24);
            `}
        }

        .details-container {
            flex-grow: 1;
            display: flex;
            gap: ${detailsContainerGap};
            flex-direction: column;
            align-items: flex-start;
            justify-content: space-between;

            ${!shouldShowProductBox &&
            css`
                justify-content: flex-end;
            `}

            ${productCardBreakpointsState.small &&
            css`
                height: fit-content;
                margin-top: ${detailsContainerMarginTop};
            `};

            ${productCardBreakpointsState.medium &&
            css`
                flex-direction: row;
                align-items: center;
                margin-top: 0;
                justify-content: space-between;
            `};
        }

        .highlights-container {
            width: 100%;
            flex-shrink: 1;

            ${productCardBreakpointsState.small &&
            css`
                width: 25rem;
            `}
        }

        .image-container {
            display: flex;
            flex-direction: column;
            gap: ${THUMBNAIL_CONTAINER_GAP};
            flex-shrink: 0;
            height: ${IMAGE_CONTAINER_HEIGHT};
        }

        .formatted-condition {
            ${theme.typography.body_small};
            strong {
                ${theme.typography.body_small_highlight};
            }
        }

        .static-container {
            display: flex;
            gap: var(--layout-spacing-12);
            width: 100%;
            height: ${staticContainerHeight};

            ${productCardBreakpointsState.small &&
            css`
                height: 100%;
            `}

            ${productCardBreakpointsState.medium &&
            css`
                gap: var(--layout-spacing-48);
                align-items: center;
            `};
        }

        .content-container {
            width: 100%;
            flex-shrink: 1;
        }

        .inline-boxlist-container {
            display: flex;
            flex-direction: column;
            justify-content: center;
            margin: calc(${backgroundPaddingSize} * -1);
            padding: ${backgroundPaddingSize};
            margin-right: 0;
            padding-right: 0;
            width: calc(100% + ${backgroundPaddingSize});
        }

        .product-box-container {
            margin: calc(${backgroundPaddingSize} * -1);
            padding: ${backgroundPaddingSize};
            margin-top: 0;
            padding-top: 0;
        }

        .buttons-container {
            display: flex;
            flex-direction: column;
            flex-shrink: 0;
            align-items: center;
            justify-content: flex-end;
            gap: var(--layout-spacing-24);
            height: ${staticContainerHeight};

            ${productCardBreakpointsState.small &&
            css`
                justify-content: center;
                height: ${IMAGE_CONTAINER_HEIGHT};
            `}
        }
    `;
};

/**
 * Styles that are only applied when measuring the component
 */
const measuringStyles = ({
    productCardBreakpointsState,
    status,
}: StyledProductCardProps) => {
    const isMeasuring = status !== BufferedMeasurementsStatus.FINISHED;

    if (!isMeasuring) {
        return;
    }

    return css`
        .expanding-card-background {
            height: 100%;
        }

        .details-container {
            height: 100%;
            margin-top: 0;

            ${productCardBreakpointsState.small &&
            css`
                height: fit-content;
            `};
        }

        .static-container {
            height: 100%;
            align-items: center;
        }

        .content-container {
            height: 100%;

            ${productCardBreakpointsState.small &&
            css`
                height: auto;
            `};
        }

        .buttons-container {
            height: 100%;
        }

        .product-box-container {
            visibility: hidden;
            height: 0;
        }
    `;
};

/**
 * The ProductCard component styles
 *
 * @param {StyledProductCardProps} props The product card properties object
 */
export const styles = (props: StyledProductCardProps) => css`
    ${baseStyles(props)}
    ${measuringStyles(props)}
`;
